// Pure orchestration logic for the run station, kept free of React and of the
// network so it can be unit-tested directly (see runner.test.ts). The React
// shell in App.tsx wires these into state; these functions make the decisions.

import type {
  StepEnvelope,
  StepResponse,
  SubmitResponse,
  Identity,
  CompletionInstruction,
} from "../api/types";

// ---- Launch parameters from the study URL ----
//
// The participant arrives at a URL the researcher's "get participant link"
// generator (Phase 7) builds. The platform is detected from the query params:
//   Prolific: ?exp=<id>&PROLIFIC_PID=...&STUDY_ID=...&SESSION_ID=...
//   MTurk:    ?exp=<id>  (+ assignmentId, hitId, workerId, turkSubmitTo, which
//             the Worker site appends to the ExternalQuestion URL)
//   SONA:     ?exp=<id>&survey_code=<per-participant code>
// `exp` is always the JOLIVE study id.

// MTurk shows the HIT to a worker before they accept it ("preview"). In that
// state assignmentId is this sentinel and no real work should run.
const MTURK_PREVIEW = "ASSIGNMENT_ID_NOT_AVAILABLE";

export interface LaunchParams {
  experimentId: string | null;
  identity: Identity;
  // MTurk preview: the worker is viewing the HIT but has not accepted it. The
  // study must NOT start; the shell shows a preview notice instead.
  preview?: boolean;
  // Researcher sandbox preview: the owner opened a pre-minted preview session
  // (created by the owner-only backend endpoint). When present, the shell drives
  // this existing session directly instead of creating a new participant one.
  sandbox?: { sessionId: string; sessionToken: string } | null;
}

export function parseLaunchParams(search: string): LaunchParams {
  const q = new URLSearchParams(search);
  const experimentId = q.get("exp");

  // ---- Researcher sandbox preview ----
  // The desktop opens the study with a pre-minted preview session. We skip
  // createSession entirely and drive that session directly, so this takes
  // precedence over any platform launch params.
  const sandboxSession = q.get("sandbox_session");
  const sandboxToken = q.get("sandbox_token");
  if (sandboxSession && sandboxToken) {
    return {
      experimentId,
      identity: { source: "sandbox" },
      sandbox: { sessionId: sandboxSession, sessionToken: sandboxToken },
    };
  }

  // ---- Prolific ----
  const pid = q.get("PROLIFIC_PID");
  if (pid) {
    const identity: Identity = { source: "prolific", external_id: pid };
    const studyId = q.get("STUDY_ID");
    const submissionId = q.get("SESSION_ID");
    if (studyId) identity.study_id = studyId;
    if (submissionId) identity.session_id = submissionId;
    return { experimentId, identity };
  }

  // ---- MTurk ----
  // assignmentId is the signal that we came from MTurk. When it equals the
  // preview sentinel, the worker has not accepted the HIT yet.
  const assignmentId = q.get("assignmentId");
  if (assignmentId) {
    if (assignmentId === MTURK_PREVIEW) {
      // Don't send a real identity for a preview; we won't start the session.
      return { experimentId, identity: { source: "mturk" }, preview: true };
    }
    const identity: Identity = { source: "mturk", assignment_id: assignmentId };
    const workerId = q.get("workerId");
    const hitId = q.get("hitId");
    const submitTo = q.get("turkSubmitTo");
    if (workerId) {
      identity.external_id = workerId;
      identity.worker_id = workerId;
    }
    if (hitId) identity.hit_id = hitId;
    if (submitTo) identity.turk_submit_to = submitTo;
    return { experimentId, identity };
  }

  // ---- SONA ----
  const surveyCode = q.get("survey_code");
  if (surveyCode) {
    const identity: Identity = {
      source: "sona",
      external_id: surveyCode,
      survey_code: surveyCode,
    };
    return { experimentId, identity };
  }

  // No platform id present: anonymous. The server mints an id when external_id
  // is omitted, so each participant is still distinguishable.
  return { experimentId, identity: { source: "anonymous" } };
}

// ---- Interpreting a GET /step response ----

export type StepInterpretation =
  | { kind: "ready"; step: StepEnvelope; stepIndex: number }
  | { kind: "pending" }
  | { kind: "ended"; completed: boolean; aborted: boolean; endReason?: string | null };

export function interpretStep(resp: StepResponse): StepInterpretation {
  if (resp.ended) {
    return {
      kind: "ended",
      completed: !!resp.completed,
      aborted: !!resp.aborted,
      ...(resp.end_reason != null ? { endReason: resp.end_reason } : {}),
    };
  }
  if (resp.step) {
    return { kind: "ready", step: resp.step, stepIndex: resp.step_index ?? 0 };
  }
  return { kind: "pending" };
}

// ---- Interpreting a POST /step (submit) response ----

export type SubmitInterpretation =
  | { kind: "next"; step: StepEnvelope; stepIndex: number }
  | { kind: "complete"; completion: CompletionInstruction }
  | { kind: "return"; returnUrl: string | null; endReason?: string | null }
  | { kind: "ended"; endReason?: string | null };

export function interpretSubmit(resp: SubmitResponse): SubmitInterpretation {
  if (resp.step) {
    return { kind: "next", step: resp.step, stepIndex: resp.step_index ?? 0 };
  }
  if (resp.completed) {
    return { kind: "complete", completion: resp.completion ?? {} };
  }
  if (resp.aborted) {
    return {
      kind: "return",
      returnUrl: resp.return_url ?? null,
      ...(resp.end_reason != null ? { endReason: resp.end_reason } : {}),
    };
  }
  return { kind: "ended", ...(resp.end_reason != null ? { endReason: resp.end_reason } : {}) };
}

// ---- Resolving a completion instruction into a concrete action ----
//
// The backend (Ship 1) labels the completion with a `kind`. We turn that into
// the one thing the shell should do. Kept pure so it's unit-tested without a
// DOM. Back-compat: an older backend sent only redirect_url with no kind, which
// we still treat as a redirect (keeps Prolific working before Ship 1 deploys).

export type CompletionAction =
  // code is the platform completion code (Prolific/SONA). It is also usually
  // embedded in the redirect url, so a SUCCESSFUL redirect never needs it shown;
  // the shell surfaces it only as a fallback when the redirect fails to navigate.
  | { kind: "redirect"; url: string; code?: string | null }
  | { kind: "mturkPost"; submitTo: string; assignmentId: string }
  // A malformed MTurk completion (missing submit_to or assignment_id): the HIT
  // cannot be submitted, so this must NOT collapse into "done" (which the shell
  // renders as success). The shell shows a loud failure screen and surfaces the
  // assignment id (when we have it) so the worker has a recourse.
  | { kind: "mturkError"; assignmentId: string | null }
  | { kind: "done" };

export function resolveCompletion(
  c: CompletionInstruction | null | undefined,
): CompletionAction {
  if (!c) return { kind: "done" };

  if (c.kind === "mturk_post") {
    // Need both fields to POST a valid externalSubmit form.
    if (c.submit_to && c.assignment_id) {
      return { kind: "mturkPost", submitTo: c.submit_to, assignmentId: c.assignment_id };
    }
    // Missing either field -> the HIT can't be submitted. Fail loud, not "done".
    return { kind: "mturkError", assignmentId: c.assignment_id ?? null };
  }

  if (c.kind === "redirect") {
    return c.redirect_url
      ? { kind: "redirect", url: c.redirect_url, ...(c.code ? { code: c.code } : {}) }
      : { kind: "done" };
  }

  if (c.kind === "done") return { kind: "done" };

  // No kind (pre-Ship-1 backend): a bare redirect_url is a redirect.
  if (c.redirect_url)
    return { kind: "redirect", url: c.redirect_url, ...(c.code ? { code: c.code } : {}) };
  return { kind: "done" };
}

// ---- Polling the loading screen until the session is built ----

export interface PollOptions {
  intervalMs?: number;
  maxAttempts?: number;
  sleepFn?: (ms: number) => Promise<void>;
}

export type PollResult =
  | { kind: "ready"; step: StepEnvelope; stepIndex: number }
  // endReason is carried so a polled end (e.g. a budget_exhausted end reached via
  // the chat-advance path) still feeds the reason-aware end screen (FE-1).
  | { kind: "ended"; completed: boolean; aborted: boolean; endReason?: string | null }
  | { kind: "timeout"; attempts: number };

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// Poll fetchStep until the server reports the step is ready or the session has
// ended, or we give up. sleepFn is injectable so tests run instantly.
export async function pollUntilReady(
  fetchStepFn: () => Promise<StepResponse>,
  opts: PollOptions = {},
): Promise<PollResult> {
  const intervalMs = opts.intervalMs ?? 1500;
  const maxAttempts = opts.maxAttempts ?? 40; // ~1 minute at 1.5s
  const sleep = opts.sleepFn ?? defaultSleep;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const interp = interpretStep(await fetchStepFn());
    if (interp.kind === "ready") {
      return { kind: "ready", step: interp.step, stepIndex: interp.stepIndex };
    }
    if (interp.kind === "ended") {
      return {
        kind: "ended",
        completed: interp.completed,
        aborted: interp.aborted,
        ...(interp.endReason != null ? { endReason: interp.endReason } : {}),
      };
    }
    if (attempt < maxAttempts) await sleep(intervalMs);
  }
  return { kind: "timeout", attempts: maxAttempts };
}
